import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject, interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';
import { environment } from 'src/environments/environment';
import { ApiService, IApiServiceConfig, ITokenResponseData } from './api';
import { AppService } from './app.service';
import { SessionStorageService, ObservablesService, TokenService, NotificationsService, INetworkError, INotificationsServiceConfig, IIdWithName, LocalStorageService, StorageKey, TranslationKey, DateTimeService, PermissionShortName, ThemeService, LicenseService } from './shared';
import { DeviceEventsPollerService } from './shared/device-events-poller.service';
import { AppRoutingGuardService } from './app-routing-guard.service';
import { AppConstants } from './app.constants';

@Component({
  selector: 'icu-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})

export class AppComponent implements OnInit, OnDestroy {
  showUsers = false;
  showJobs = false;
  showDevices = false;
  showConfiguration = false;
  showAlarms = false;
  isLoggedIn = false;
  menuDashboardDisabled = true;
  loggedUsername = '';
  supportedLanguages: IIdWithName[] = [];
  unacknowledgedAlarmsCount = 0;
  utcDate: string = '';
  utcTime: Date | null = null;
  showCalendars: boolean = false;
  onMouseOverCalendars: boolean = false;
  languageMenu: boolean = false;
  lightMenu: boolean = false;
  onMouseOverLanguageMenu: boolean = false;
  isMouseOverLightMenu: boolean = false;
  leftMenuToggleClick: boolean = true;
  toggleStatus: boolean = true;

  private destroy$ = new Subject<void>();

  constructor(
    private apiSvc: ApiService,
    private obsSvc: ObservablesService,
    private sessStorageSvc: SessionStorageService,
    private router: Router,
    private tokenSvc: TokenService,
    private deviceEventsSvc: DeviceEventsPollerService,
    private notificationsSvc: NotificationsService,
    private appSvc: AppService,
    private transSvc: TranslocoService,
    private localStorageSvc: LocalStorageService,
    private dateTimeSvc: DateTimeService,
    private themeService: ThemeService,
    private appRoutingGuard: AppRoutingGuardService,
    private licenseSvc: LicenseService,
    private titleSvc: Title
  ) {
  }

  // Product name shown as Title on authenticated pages (never on public login page).
  private readonly productTitle = 'iCU Server';

  async ngOnInit(): Promise<void> {
    this.localStorageSvc.setKeyPrefix(AppConstants.STORAGE_KEY_PREFIX);
    this.sessStorageSvc.setKeyPrefix(AppConstants.STORAGE_KEY_PREFIX);
    this.refreshTime();
    interval(AppConstants.TIME_DISPLAY_UPDATE_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshTime());
    this.themeService.initializeTheme();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(e => this.applyLoginChrome((e as NavigationEnd).urlAfterRedirects));
    const language = this.localStorageSvc.getItem<string>(StorageKey.language) || AppConstants.DEFAULT_LANGUAGE;
    this.transSvc.setActiveLang(language);
    document.documentElement.lang = language;// Expose active language to CSS (to set --icu-sidebar-width).
    this.supportedLanguages.push(...AppConstants.SUPPORTED_LANGUAGES);
    const notificationsSvcConfig: INotificationsServiceConfig = {
      duration: AppConstants.NOTIFICATION_DURATION_MS,
      infoClasses: 'icu-notification-info',
      successClasses: 'icu-notification-success',
      warnClasses: 'icu-notification-warn',
      errorClasses: 'icu-notification-error'
    };
    this.notificationsSvc.init(notificationsSvcConfig);
    this.showLicenseGraceWarningIfNeeded();
    this.obsSvc.getNetworkResponseError()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: err => this.processNetworkError(err)
      });
    this.obsSvc.getUnacknowledgedAlarms()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: args => this.unacknowledgedAlarmsCount = args.count
      });
    const apiSvcConfig: IApiServiceConfig = {
      baseApiUrl: environment.baseApiUrl
    };
    this.apiSvc.init(apiSvcConfig);
    const authRes = this.sessStorageSvc.getItem(StorageKey.authResponse) as ITokenResponseData;
    const canUseToken = this.processAuthenticateResponse(authRes);
    if (canUseToken) {
      await this.loadClientSettings();
      this.applyAccess();
      // We have token in the session storage
      if (this.isCurrentLocationBaseHref()) {
        // There is no specific url - navigate to dashboard
        this.navigateToDashboard();
      }
    } else {
      this.applyAccess();
      // Cannot use the token (not found, expired or corrupted)
      this.navigateToLogin();
    }
    this.obsSvc.getAuthenticated()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: authRes => this.processUserAuthenticatedResult(authRes)
      });
    this.startTimeout();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyLoginChrome(url: string): void {
    const onLoginPage = url.startsWith('/login');
    document.body.classList.toggle('icu-authenticated', !onLoginPage);
    this.titleSvc.setTitle(onLoginPage ? 'Login' : this.productTitle);
  }

  // If license has expired and server is inside its grace period, show a transient warning toast.
  private showLicenseGraceWarningIfNeeded(): void {
    if (!this.licenseSvc.isInGracePeriod) {
      return;
    }
    const days = this.licenseSvc.graceDaysRemaining;
    this.notificationsSvc.showWarn(
      `License expired - the server will keep running for ${days} more day(s). ` +
      `Please install a renewed license to avoid an outage.`
    );
  }

  onChangeToDarkMode(): void {
    this.themeService.changeToDarkMode();
  }

  onChangeToLightMode(): void {
    this.themeService.changeToLightMode();
  }

  private async loadClientSettings(): Promise<void> {
    const clientSettings = await this.apiSvc.getClientSettings();
    this.obsSvc.setClientSettings(clientSettings);
    this.deviceEventsSvc.start(clientSettings.deviceAlarmsPollIntervalSeconds * 1000);
  }

  async onLanguageChanged(lang: IIdWithName): Promise<void> {
    const currentLang = this.transSvc.getActiveLang();
    // Only change if language is different
    if (currentLang === lang.id) {
      return;
    }
    this.localStorageSvc.setItem(StorageKey.language, lang.id);
    window.location.reload();
  }

  onGoToDashboardClick(): void {
    this.navigateToDashboard();
  }

  onLogoutClick(): void {
    this.setUnauthorized();
    this.navigateToLogin();
  }

  refreshTime(): void {
    const currentDate = this.dateTimeSvc.getCurrentDate();
    this.utcTime = currentDate;
    const day = String(currentDate.getUTCDate()).padStart(2, '0');
    const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
    const year = currentDate.getUTCFullYear();
    this.utcDate = `${day}-${month}-${year}`;
  }

  private setUnauthorized(): void {
    this.isLoggedIn = false;
    this.appRoutingGuard.isLoggedIn = false;
    this.deviceEventsSvc.stop();
    this.apiSvc.clearAuthorizationData();
    this.tokenSvc.clearToken();
    this.sessStorageSvc.removeItem(StorageKey.authResponse);
    this.setLoggedInMenusDisabledState(true);
  }

  private async processUserAuthenticatedResult(authRes: ITokenResponseData): Promise<void> {
    const canUseToken = this.processAuthenticateResponse(authRes);
    if (canUseToken) {
      this.sessStorageSvc.setItem(StorageKey.authResponse, authRes);
      await this.loadClientSettings();
      this.applyAccess();
      // TODO: If there is no specific url - navigate to dashboard
      this.navigateToDashboard();
    } else {
      this.setUnauthorized();
    }
  }

  private processAuthenticateResponse(authRes: ITokenResponseData): boolean {
    if (authRes?.access_token) {
      if (this.tokenSvc.hasExpired(authRes.access_token)) {
        this.setUnauthorized();
        return false;
      }
      this.sessStorageSvc.setItem(StorageKey.authResponse, authRes);
      this.useToken(authRes.access_token, authRes.token_type);
      this.setLoggedInMenusDisabledState(false);
      return true;
    } else {
      // TODO: No token
      this.setUnauthorized();
    }
    return false;
  }

  private useToken(token: string, tokenType: string): void {
    this.apiSvc.setAuthorizationHeaderValue(tokenType + ' ' + token);
    const tokenInfo = this.tokenSvc.setToken(token);
    this.loggedUsername = tokenInfo.data.username;
    this.isLoggedIn = true;
    this.appRoutingGuard.isLoggedIn = true;
  }

  private navigateToDashboard(): void {
    this.router.navigate(['dashboard']);
  }

  private navigateToLogin(): void {
    // TODO: For some reason the option { queryParamsHandling: 'preserve' } does not work
    const queryParams = this.router.getCurrentNavigation()?.extractedUrl.queryParams;
    this.router.navigate(['login'], { queryParams: queryParams });
  }

  private isCurrentLocationBaseHref(): boolean {
    // TODO: Look at document <base href="...">
    return window.location.pathname === '/';
  }

  private setLoggedInMenusDisabledState(disabled: boolean): void {
    this.menuDashboardDisabled = disabled;
  }

  private startTimeout(): void {
    window.setTimeout(() => this.timeoutElapsed(), AppConstants.TOKEN_CHECK_INTERVAL_MS);
  }

  private async timeoutElapsed(): Promise<void> {
    try {
      await this.refreshTokenIfAboutToExpire();
    } finally {
      this.startTimeout();
    }
  }

  private async refreshTokenIfAboutToExpire(): Promise<void> {
    // Check if token is about to expire
    const expiresAtSeconds = this.tokenSvc.getExpiresAt();
    if (expiresAtSeconds) {
      const now = this.dateTimeSvc.getNow();
      const issuedAt = this.tokenSvc.getIssuedAt();
      if (!issuedAt) {
        // Cannot determine when token was issued, skip refresh
        return;
      }
      const issuedAtMilliseconds = issuedAt * 1000;
      const lastRequestMadeAtMilliseconds = this.apiSvc.getLastRequestTimestamp() || 0;
      if (lastRequestMadeAtMilliseconds < issuedAtMilliseconds) {
        // The last request was made before the token has been issued - no activity for a long time so don't refresh the token
        return;
      }
      // The value must be multiplied by 1000
      const expiresAtMilliseconds = expiresAtSeconds * 1000;
      const diff = expiresAtMilliseconds - now;
      // diff <= 0 means the token is already expired
      if (diff > 0 && diff < AppConstants.TOKEN_REFRESH_TOLERANCE_MS) {
        const res = await this.apiSvc.refreshToken();
        if (res.access_token) {
          this.processAuthenticateResponse(res);
        }
      }
    }
  }

  private processNetworkError(err: INetworkError): void {
    if (err.status === 401) {
      // Unauthorized
      this.notificationsSvc.showError(this.transSvc.translate(TranslationKey.notAuthenticated));
      this.setUnauthorized();
      this.navigateToLogin();
    } else if (err.status === 403) {
      this.notificationsSvc.showWarn(this.transSvc.translate(TranslationKey.insufficientPermissions));
    } else {
      this.notificationsSvc.showError(this.appSvc.getTextFromNetworkError(err));
    }
  }

  private applyAccess(): void {
    this.showUsers = this.tokenSvc.hasAccess([PermissionShortName.ManageUsers]);
    this.showJobs = this.tokenSvc.hasAccess([PermissionShortName.CreateMissions, PermissionShortName.MissionEligible]);
    this.showDevices = this.tokenSvc.hasAccess([PermissionShortName.ManageTrackingUnits]);
    this.showConfiguration = this.tokenSvc.hasAccess([PermissionShortName.SystemConfig]);
    this.showAlarms = this.tokenSvc.hasAccess([PermissionShortName.MissionEligible]);
  }

  showCalendar(): void {
    this.showCalendars = true;
  }

  leaveCalendar(): void {
    setTimeout(() => {
      this.showCalendars = false;
    }, AppConstants.MENU_CLOSE_DELAY_MS);
  }

  onMouseOverCalendar(): void {
    this.onMouseOverCalendars = true;
  }

  onMouseOutCalendar(): void {
    this.onMouseOverCalendars = false;
  }

  showMenu(): void {
    this.languageMenu = true;
  }

  leaveMenu(): void {
    setTimeout(() => {
      this.languageMenu = false;
    }, AppConstants.MENU_CLOSE_DELAY_MS);
  }

  onMouseOverMenu(): void {
    this.onMouseOverLanguageMenu = true;
  }

  onMouseOutMenu(): void {
    this.onMouseOverLanguageMenu = false;
  }

  openLightMenu(): void {
    this.lightMenu = true;
  }

  leaveLightMenu(): void {
    setTimeout(() => {
      this.lightMenu = false;
    }, AppConstants.MENU_CLOSE_DELAY_MS);
  }

  onMouseOverLightMenu(): void {
    this.isMouseOverLightMenu = true;
  }

  onMouseOutLightMenu(): void {
    this.isMouseOverLightMenu = false;
  }

  toggleLeftMenu(): void {
    this.leftMenuToggleClick = !this.leftMenuToggleClick;
    this.toggleStatus = this.leftMenuToggleClick;
    this.obsSvc.setmenuToggle(this.leftMenuToggleClick);
  }

}